import {ProductModel} from "../../models/product";
import React, {useEffect, useRef, useState} from "react";
import {Controller, Grid, Navigation, Pagination, Scrollbar, Swiper as SwiperClass} from "swiper";
import {NavigationOptions} from "swiper/types";
import {ProductCardView} from "./ProductCardView";
import {ArrowBackIcon, ArrowNextIcon} from "../icons";
import {Swiper, SwiperSlide} from "swiper/react";
import Link from "next/link"
import {useRouter} from "next/router";
import {useModalRouting} from "../../utils/useModalRouting";

export type StoreSectionCarouselType = 'row' | 'grid'

export interface StoreSectionCarouselViewProps {
  products: ProductModel[]
  carouselType: StoreSectionCarouselType
  loading: boolean
}

export const StoreSectionCarouselView: React.FC<StoreSectionCarouselViewProps> = ({ products, loading, carouselType }) => {
  const [controlledSwiper, setControlledSwiper] = useState<SwiperClass>();

  const [nextButtonActive, setNextButtonActive] = useState<boolean>(false);
  const [nextButtonVisible, setNextButtonVisible] = useState<boolean>(false);
  const [prevButtonActive, setPrevButtonActive] = useState<boolean>(false);
  const [prevButtonVisible, setPrevButtonVisible] = useState<boolean>(false);

  const nextRef = useRef(null)
  const prevRef = useRef(null)

  const { createRoute } = useModalRouting();

  useEffect(() => {
    updateArrowDisplay(controlledSwiper)
  }, [loading, products])

  const updateArrowDisplay = (swiper?: SwiperClass) => {
    if (carouselType === 'grid' || loading) {
      setPrevButtonActive(false)
      setPrevButtonVisible(false)
      setNextButtonActive(false)
      setNextButtonVisible(false)
      return
    }
    if (!swiper) return

    setPrevButtonVisible(swiper.activeIndex > 0)
    setNextButtonVisible((swiper.params.slidesPerView as number) < products.length)

    setPrevButtonActive(swiper.activeIndex > 0)
    setNextButtonActive(swiper.activeIndex + (swiper.params.slidesPerView as number) < products.length)
  }

  if (carouselType === 'grid') {
    return <div className="mt-6 grid gap-6 md:gap-8 lg:gap-10 justify-items-start grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {products.map((i, index) => {
        return <div key={i.id + '_' + index.toString()} className="flex w-full h-full items-start justify-start">
          <div className="flex w-full h-full justify-start">
            <ProductCardView loading={loading} product={i} />
          </div>
        </div>
      })}
    </div>
  }

  return <div className="relative overflow-y-visible">
    <Swiper
      onInit={(swiper) => {
        (swiper.params.navigation as NavigationOptions).prevEl = prevRef.current;
        (swiper.params.navigation as NavigationOptions).nextEl = nextRef.current;
        swiper.navigation.init();
        swiper.navigation.update();

        updateArrowDisplay(swiper)
      }}
      style={{ paddingTop: '1rem', paddingRight: '1rem' }}
      className="pt-4 pr-4"
      modules={[Navigation, Grid, Pagination, Scrollbar, Controller]}
      onSwiper={swiper => {
        setControlledSwiper(swiper)
      }}
      spaceBetween={30}
      slidesPerView={6}
      slidesPerGroup={1}
      breakpoints={{
        240: {
          slidesPerView: 1,
          spaceBetween: 10,
          touchRatio: 1,
          scrollbar: { draggable: true }
        },
        320: {
          slidesPerView: 2,
          spaceBetween: 20,
          touchRatio: 1,
          scrollbar: { draggable: true }
        },
        480: {
          slidesPerView: 2,
          spaceBetween: 20,
          touchRatio: 1,
          scrollbar: { draggable: true }
        },
        640: {
          slidesPerView: 3,
          spaceBetween: 20,
          touchRatio: 1,
          scrollbar: { draggable: true }
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 30,
          touchRatio: 0,
          scrollbar: { draggable: false }
        },
        1024: {
          slidesPerView: 5,
          spaceBetween: 35,
          touchRatio: 0,
          scrollbar: { draggable: false }
        },
        1280: {
          slidesPerView: 6,
          spaceBetween: 40,
          touchRatio: 0,
          scrollbar: { draggable: false }
        }
      }}
      preventClicks={false}
      preventClicksPropagation={false}
      pagination={{ clickable: true }}
      scrollbar={{ draggable: true }}
      onDragEnd={() => updateArrowDisplay(controlledSwiper)}
      onSlideChange={updateArrowDisplay}
    >
      {products.map((i, index) => {
        return <SwiperSlide key={i.id + '_' + index.toString()}>
          <div key={i.id + '_' + index.toString()} className="flex items-center justify-center">
            <div className="flex w-full justify-center">
              <ProductCardView loading={loading} product={i} />
            </div>
          </div>
        </SwiperSlide>
      })}
    </Swiper>
    <div
      onClick={(e) => {
        e.stopPropagation()
        const nextIndex = controlledSwiper?.activeIndex ?? 0
        const slidesPerView = controlledSwiper?.params.slidesPerView as number ?? 0
        controlledSwiper?.slideTo(nextIndex - slidesPerView)
      }}
      className={`${prevButtonVisible ? '' : 'hidden'} ${prevButtonActive ? 'transition-all opacity-100 cursor-pointer hover:bg-fg-gray-surface' : 'opacity-50'} absolute -left-2 lg:-left-6 ring-1 ring-fg-gray-surface inset-y-0 top-1/2 -mt-12 z-10 flex items-center justify-center bg-white w-9 h-9 lg:w-12 lg:h-12 p-2 lg:p-3.5 shadow-xl m-auto rounded-full`}
      ref={prevRef}>
      <ArrowBackIcon className="h-3 w-3 lg:h-4 lg:w-4" />
    </div>
    <div
      onClick={(e) => {
        e.stopPropagation()
        const nextIndex = controlledSwiper?.activeIndex ?? 0
        const slidesPerView = controlledSwiper?.params.slidesPerView as number ?? 0
        controlledSwiper?.slideTo(nextIndex + slidesPerView)
      }}
      className={`${nextButtonVisible ? '' : 'hidden'} ${nextButtonActive ? 'transition-all opacity-100 cursor-pointer hover:bg-fg-gray-surface' : 'opacity-50'} absolute -right-2 lg:-right-6 ring-1 ring-fg-gray-surface inset-y-0 top-1/2 -mt-12 z-10 flex items-center justify-center bg-white w-9 h-9 lg:w-12 lg:h-12 p-2 lg:p-3.5 shadow-xl m-auto rounded-full`}
      ref={nextRef}>
      <ArrowNextIcon className="h-3 w-3 lg:h-4 lg:w-4" />
    </div>
  </div>
}